/**
 * Client-side auth state for the SPA.
 *
 * The SDK hooks (`useTensileAuth`, `useAppVault`) are the storage primitives, but the pages need
 * one shared instance: `useTensileAuth` keeps its state per hook call and only syncs across tabs,
 * so two independent calls would disagree after sign-in in the same tab. This provider owns the
 * single instance and adds the server round-trips + the app-vault unlock.
 *
 * Every sign-in is now two factors: an email code, and an assertion from a credential the worker
 * can verify against a stored public key. The code arrives first because the challenge the
 * authenticator signs is issued inside the code's ticket — neither half is useful alone.
 *
 * The Master Identity seed lives in memory only. The derived app-scoped key is stored in
 * sessionStorage by `useAppVault` so a reload in the same tab keeps the vault usable.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  assertPasskeyWithPrf,
  computeIdentityFingerprint,
  describeAaguid,
  exportAppScopedSecretKeyB64,
  generateMasterIdentitySeed,
  legacyDigestPrfOutput,
  registerPasskeyWithPrf,
  resolveSharedPrfSaltB64,
  SHARED_PRF_SALT_B64,
  unwrapMasterKeyFromDevice,
  wrapMasterKeyForDevice,
  type Bytes,
  type TensileAuthSession,
  type WrappedDeviceMasterKey,
} from "@tensilelabs/auth-client";
import { useAppVault, useTensileAuth } from "@tensilelabs/auth-client/react";
import {
  fetchAccount,
  passkeyLogin,
  registerPasskey,
  requestOtp,
  unlockChallenge,
  unlockWithAssertion,
  verifyOtp,
  type AccountSnapshot,
  type OtpTicket,
  type OtpVerification,
} from "../api";
import { authDebug, ceremonyDebug, describeClientCapabilities, describeEnvelope } from "./debug";

export const APP_ID = "taskjinn";

const RP_ID = "tensilelabs.com";

interface AuthContextValue {
  session: TensileAuthSession | null;
  account: AccountSnapshot | null;
  identityFingerprint: string | null;
  vaultUnlocked: boolean;
  appScopedSecretKeyB64: string | null;
  busy: boolean;
  error: string | null;
  isAdmin: boolean;
  clearError: () => void;
  requestOtp: (email: string) => Promise<{ ticket: OtpTicket; devOtpPreview?: string }>;
  /** Creates the account's first passkey (or replaces every device record, on the repair path). */
  completeSignup: (
    verification: OtpVerification,
    options: { deviceName: string; replaceDevices?: boolean },
  ) => Promise<void>;
  /** Signs in with the passkey that belongs to this device. */
  signIn: (verification: OtpVerification, options?: { allowHybrid?: boolean }) => Promise<void>;
  /** Reopens the vault for an existing session, without another email code. */
  unlockVault: () => Promise<void>;
  signOut: () => void;
  refreshAccount: () => Promise<AccountSnapshot | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Opens a device envelope with the passkey's PRF output, tolerating envelopes written by the old
 * registration path.
 *
 * Registration used to seal the seed under `SHA-256(rawId || prfSalt)` because browsers do not
 * evaluate `prf.eval` during create(). Those envelopes can only be opened by re-deriving that same
 * digest, so they get one attempt before failing.
 */
async function unwrapDeviceSeed(
  envelope: WrappedDeviceMasterKey,
  prfOutput: Bytes,
): Promise<{ seed: Bytes; legacy: boolean }> {
  try {
    return { seed: await unwrapMasterKeyFromDevice(envelope, prfOutput), legacy: false };
  } catch {
    const legacyOutput = await legacyDigestPrfOutput({
      credentialIdB64: envelope.credential_id_b64,
      prfSaltB64: envelope.prf_salt_b64,
    });
    return { seed: await unwrapMasterKeyFromDevice(envelope, legacyOutput), legacy: true };
  }
}

/** Which device this browser last signed in with, so its own envelope's salt is preferred. */
const DEVICE_HINT_KEY = `tensile.device.${APP_ID}`;

function readDeviceHint(): string | null {
  try {
    return localStorage.getItem(DEVICE_HINT_KEY);
  } catch {
    return null;
  }
}

function rememberDeviceHint(deviceId: string): void {
  try {
    localStorage.setItem(DEVICE_HINT_KEY, deviceId);
  } catch {
    // Private mode / storage disabled: the next sign-in just guesses the salt again.
  }
}

/**
 * No credential for this rpId lives on this device.
 *
 * Distinct from a dismissed prompt: the browser rejected the ceremony without ever asking, which is
 * the difference between "approve the prompt" and "this passkey is somewhere else".
 */
export class NoLocalPasskeyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NoLocalPasskeyError";
  }
}

/**
 * Asserts the account's passkey for a challenge the server issued inside the email-code ticket.
 *
 * The salt has to be chosen before the ceremony, and legacy accounts may hold envelopes sealed
 * under different ones, so the choice is: this browser's remembered device, else the salt every
 * candidate agrees on, else the shared one — with one retry against the matched envelope's own salt.
 */
async function assertForLogin(params: {
  email: string;
  challengeB64: string;
  envelopes: WrappedDeviceMasterKey[];
  allowHybrid?: boolean;
}): Promise<{
  envelope: WrappedDeviceMasterKey;
  prfOutput: Bytes;
  assertion: NonNullable<Awaited<ReturnType<typeof assertPasskeyWithPrf>>["assertionProof"]>;
  saltB64: string;
}> {
  const { envelopes } = params;
  const credentialIds = envelopes.map((envelope) => envelope.credential_id_b64);
  const hinted = envelopes.find((envelope) => envelope.device_id === readDeviceHint());
  let saltB64 = hinted?.prf_salt_b64 ?? resolveSharedPrfSaltB64(envelopes) ?? SHARED_PRF_SALT_B64;

  const run = (transports?: AuthenticatorTransport[]) =>
    assertPasskeyWithPrf({
      email: params.email,
      challengeB64: params.challengeB64,
      prfSaltB64: saltB64,
      allowCredentialIdsB64: credentialIds,
      ...(transports ? { transports } : {}),
      rpId: RP_ID,
      onDebug: ceremonyDebug,
    });

  const startedAt = Date.now();
  let ceremony: Awaited<ReturnType<typeof run>>;
  try {
    // Local authenticator only, so a browser without the credential fails immediately instead of
    // offering the cross-device QR as if it were the next step.
    ceremony = await run(["internal"]);
  } catch (cause) {
    const elapsedMs = Date.now() - startedAt;
    authDebug("login:local-passkey-missing", {
      elapsedMs,
      allowHybrid: Boolean(params.allowHybrid),
      error: cause instanceof Error ? cause.message : String(cause),
    });
    if (!params.allowHybrid) {
      throw new NoLocalPasskeyError(
        elapsedMs < 1500
          ? "This device has no passkey for TaskJinn. Use a phone or tablet that has one, or register a passkey on this device."
          : "Passkey verification was cancelled or timed out. Approve the browser prompt to continue.",
      );
    }
    ceremony = await run();
  }

  const envelope =
    envelopes.find((candidate) => candidate.credential_id_b64 === ceremony.credentialIdB64) ??
    envelopes[0];

  return {
    envelope,
    prfOutput: ceremony.prfOutput,
    assertion: ceremony.assertionProof!,
    saltB64,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useTensileAuth();
  const vault = useAppVault(APP_ID);
  const { unlock: unlockVaultKey, lock: lockVaultKey } = vault;
  const [identityFingerprint, setIdentityFingerprint] = useState<string | null>(null);
  const [account, setAccount] = useState<AccountSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const session = useMemo<TensileAuthSession | null>(() => {
    if (!auth.token) return null;
    return {
      token: auth.token,
      email: auth.email,
      accountId: auth.accountId,
      role: auth.role,
    };
  }, [auth.token, auth.email, auth.accountId, auth.role]);

  const unlockWithSeed = useCallback(
    async (seed: Bytes) => {
      const appKey = await exportAppScopedSecretKeyB64(seed, APP_ID);
      unlockVaultKey(appKey);
      setIdentityFingerprint(await computeIdentityFingerprint(seed));
    },
    [unlockVaultKey],
  );

  const refreshAccount = useCallback(async () => {
    if (!auth.token) return null;
    try {
      const snapshot = await fetchAccount();
      setAccount(snapshot);
      return snapshot;
    } catch (cause) {
      // A stale token must not leave the UI claiming to be signed in.
      if (cause instanceof Error && /unauthorized|401/i.test(cause.message)) {
        auth.signOut();
        setAccount(null);
      }
      return null;
    }
  }, [auth]);

  // Load the account on mount and whenever the token changes (sign-in, unlock, other tab).
  useEffect(() => {
    if (auth.token) void refreshAccount();
    else setAccount(null);
  }, [auth.token, refreshAccount]);

  const run = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
    setBusy(true);
    setError(null);
    try {
      return await action();
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Something went wrong";
      setError(message);
      throw cause;
    } finally {
      setBusy(false);
    }
  }, []);

  const completeSignup = useCallback<AuthContextValue["completeSignup"]>(
    (verification, options) =>
      run(async () => {
        const email = verification.login_ticket.email;
        authDebug("signup:start", {
          email,
          replaceDevices: Boolean(options.replaceDevices),
          existingDevices: verification.device_envelopes?.length ?? 0,
        });

        const ceremony = await registerPasskeyWithPrf({
          email,
          userId: email,
          challengeB64: verification.challenge_b64,
          rpId: verification.webauthn_rp_id,
          onDebug: ceremonyDebug,
        });
        if (!ceremony.registrationProof) {
          throw new Error("The browser did not return the material needed to register this passkey.");
        }

        const seed = generateMasterIdentitySeed();
        const deviceEnvelope = await wrapMasterKeyForDevice({
          masterSeed: seed,
          deviceId: `dev-${crypto.randomUUID().slice(0, 8)}`,
          deviceName: options.deviceName,
          credentialIdB64: ceremony.credentialIdB64,
          passkeyPrfOutput: ceremony.prfOutput,
          prfSalt: ceremony.prfSalt,
        });
        const registered = await registerPasskey({
          email,
          displayName: email.split("@")[0],
          deviceEnvelope,
          registration: ceremony.registrationProof,
          loginTicket: verification.login_ticket,
          replaceDevices: options.replaceDevices,
        });
        authDebug("signup:registered", {
          credentialIdB64: ceremony.credentialIdB64,
          rpIdUsed: ceremony.rpIdUsed,
          aaguid: ceremony.aaguid ?? null,
          aaguidName: describeAaguid(ceremony.aaguid),
          transports: ceremony.transports ?? null,
        });

        auth.setSession({
          token: registered.token,
          email,
          accountId: registered.account_id,
          role: registered.role,
        });
        rememberDeviceHint(deviceEnvelope.device_id);
        await unlockWithSeed(seed);
        await refreshAccount();
      }),
    [auth, refreshAccount, run, unlockWithSeed],
  );

  const signIn = useCallback<AuthContextValue["signIn"]>(
    (verification, options) =>
      run(async () => {
        const email = verification.login_ticket.email;
        const envelopes = verification.device_envelopes ?? [];
        authDebug("login:response", {
          email,
          envelopeCount: envelopes.length,
          deviceHint: readDeviceHint(),
        });
        envelopes.forEach((envelope, index) => {
          authDebug(`login:envelope[${index}]`, describeEnvelope(envelope));
        });
        authDebug("login:capabilities", await describeClientCapabilities());

        if (envelopes.length === 0) {
          throw new Error(
            "This account has no passkey yet. Use “create a passkey” to register one on this device.",
          );
        }

        const { envelope, prfOutput, assertion, saltB64 } = await assertForLogin({
          email,
          challengeB64: verification.challenge_b64,
          envelopes,
          allowHybrid: options?.allowHybrid,
        });

        // The server verifies the signature against the public key it stored at registration and
        // mints the session only if it holds — the client's word is no longer enough.
        const login = await passkeyLogin({
          email,
          loginTicket: verification.login_ticket,
          assertion,
        });
        const target = login.device_envelope ?? envelope;

        let opened: { seed: Bytes; legacy: boolean };
        try {
          opened = await unwrapDeviceSeed(target, prfOutput);
        } catch (cause) {
          if (target.prf_salt_b64 === saltB64) throw cause;
          // Sealed under its own salt (registered before the shared salt landed): one more
          // assertion, with the same server-issued challenge, against that salt.
          authDebug("login:retry-with-envelope-salt", {
            envelopeSaltB64: target.prf_salt_b64,
            triedSaltB64: saltB64,
          });
          const retry = await assertPasskeyWithPrf({
            email,
            challengeB64: verification.challenge_b64,
            prfSaltB64: target.prf_salt_b64,
            credentialIdB64: target.credential_id_b64,
            transports: ["internal"],
            rpId: RP_ID,
            onDebug: ceremonyDebug,
          });
          opened = await unwrapDeviceSeed(target, retry.prfOutput);
        }

        auth.setSession({
          token: login.token,
          email,
          accountId: login.account_id,
          role: login.role,
        });
        rememberDeviceHint(envelope.device_id);
        await unlockWithSeed(opened.seed);
        await refreshAccount();
      }),
    [auth, refreshAccount, run, unlockWithSeed],
  );

  const unlockVault = useCallback<AuthContextValue["unlockVault"]>(
    () =>
      run(async () => {
        if (!auth.token) throw new Error("Sign in before unlocking the vault");
        const { ticket, challenge_b64, device_envelope } = await unlockChallenge();
        const ceremony = await assertPasskeyWithPrf({
          email: auth.email ?? "",
          challengeB64: challenge_b64,
          prfSaltB64: device_envelope.prf_salt_b64,
          credentialIdB64: device_envelope.credential_id_b64,
          transports: ["internal"],
          rpId: RP_ID,
          onDebug: ceremonyDebug,
        });
        if (!ceremony.assertionProof) throw new Error("The browser returned no assertion");
        const verified = await unlockWithAssertion({ ticket, assertion: ceremony.assertionProof });
        const { seed } = await unwrapDeviceSeed(verified.device_envelope, ceremony.prfOutput);
        await unlockWithSeed(seed);
        await refreshAccount();
      }),
    [auth.email, auth.token, refreshAccount, run, unlockWithSeed],
  );

  const signOut = useCallback(() => {
    auth.signOut();
    lockVaultKey();
    setIdentityFingerprint(null);
    setAccount(null);
    setError(null);
  }, [auth, lockVaultKey]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      account,
      identityFingerprint,
      vaultUnlocked: vault.unlocked,
      appScopedSecretKeyB64: vault.appScopedSecretKeyB64,
      busy,
      error,
      isAdmin: session?.role === "owner" || session?.role === "admin",
      clearError: () => setError(null),
      requestOtp: async (email: string) => {
        const response = await requestOtp(email.trim().toLowerCase());
        return { ticket: response.ticket, devOtpPreview: response.dev_otp_preview };
      },
      completeSignup,
      signIn,
      unlockVault,
      signOut,
      refreshAccount,
    }),
    [
      session,
      account,
      identityFingerprint,
      vault.unlocked,
      vault.appScopedSecretKeyB64,
      busy,
      error,
      completeSignup,
      signIn,
      unlockVault,
      signOut,
      refreshAccount,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside an AuthProvider");
  return value;
}

/** Re-exported so the login page can verify the code before choosing a ceremony. */
export { verifyOtp };
export type { OtpVerification };
